import { SecurityContext } from 'auth/contexts/SecurityContext';
import { useContext } from 'react';
import { FetchDataResponse, fetchData } from '../models/fetch-data';

function fetchBuilder(token: string, removeToken: () => void) {
  return async function buildFetchRequest<T>(params: {
    method: string;
    url: string;
    headers?: HeadersInit;
    body?: any;
  }): Promise<FetchDataResponse<T>> {
    const { method, url, body, headers } = params;
    try {
      return await fetchData<T>(
        method,
        url,
        Object.assign(headers || {}, {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
        body,
      );
    } catch (error) {
      if (error.statusCode === 401) {
        removeToken();
      }
      throw error;
    }
  };
}

const state: {
  token: string;
  removeToken: () => void;
} = {
  token: '',
  removeToken: null,
};

class FetchBuilder {
  setup(token: string, removeToken: () => void) {
    state.token = token;
    state.removeToken = removeToken;
  }

  async post<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return fetchBuilder(
      state.token,
      state.removeToken,
    )({
      method: 'POST',
      url,
      body,
    });
  }

  async get<T>(url: string): Promise<FetchDataResponse<T>> {
    return fetchBuilder(state.token, state.removeToken)({ method: 'GET', url });
  }

  async put<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return fetchBuilder(
      state.token,
      state.removeToken,
    )({
      method: 'PUT',
      url,
      body,
    });
  }

  async del<T>(url: string): Promise<FetchDataResponse<T>> {
    return fetchBuilder(state.token, state.removeToken)({ method: 'DELETE', url });
  }
}

const fetchBuider = new FetchBuilder();

export function useFetch() {
  const { accessToken, removeTokens } = useContext(SecurityContext);

  fetchBuider.setup(accessToken, removeTokens);

  return {
    post: fetchBuider.post,
    get: fetchBuider.get,
    put: fetchBuider.put,
    del: fetchBuider.del,
  };
}
